import { useState } from "react";
import { POSTER_PAGE, type PosterDoc, type PosterImageElement, type PosterStateResponse } from "@studio/shared";
import { InlineSpinner } from "../../components/InlineSpinner";
import { ErrorPage, InlineError, LoadingPage, StudioShell } from "../../components/StudioShell";
import { ApiError, apiRequest } from "../../lib/api";
import { compileMarker } from "../../lib/arMarker";
import { savePosterDoc, usePoster } from "../../poster/api";
import { AUTO_QR, withoutQr } from "../../poster/autoQr";
import { loadPosterFonts } from "../../poster/fonts";
import { PosterPreview } from "../../poster/PosterPreview";
import { drawPosterToCanvas, imageKey, preloadImages } from "../../poster/render";
import { goPosterStep } from "../../poster/steps";
import { starterDoc } from "../../poster/template";
import { PosterDownloadDialog, posterImageLoader } from "./PosterDownloadDialog";
import { PosterResumeGate } from "./PosterResumeGate";

/**
 * 포스터 4단계 「인쇄 확정」(목업 s7 · D7).
 *
 * 🔑 확정하는 순간의 포스터로 AR 인식 파일(.mind)을 학생 브라우저가 굽는다 — 서버에는 브라우저가 없다.
 *    대상 = 포스터에서 가장 큰 그림을 **인쇄될 모습 그대로**(위에 얹은 글·스티커 포함) 잘라 낸 이미지.
 * 🔑 확정 뒤 고치거나 그림을 다시 그리면 확정이 풀린다(서버가 읽을 때 계산 · D14) — 다시 확정하고 다시 인쇄.
 * 🔑 인식 파일은 **QR 자리를 흰색으로 비운** 페이지에서 굽는다(PLAN-poster-class-qr D6). 반 QR 은 반 학생 모두
 *    똑같아서, 큰 그림이 구석까지 덮으면 모든 학생 인식 파일에 같은 QR 무늬가 들어가 남의 영상이 나올 수 있다.
 *    인쇄물의 QR 무늬는 인식 파일에 없는 «가림» 이 된다. 그래서 QR 에 절반 넘게 가린 그림은 확정하지 않는다.
 */

/** QR 이 가려도 되는 한계 — 잘라 낸 칸에서 QR 자리를 뺀 «보이는 넓이» 가 이 비율보다 작으면 거절(D6). */
export const MARKER_MIN_VISIBLE = 0.5;
export const QR_COVERS_PICTURE_MESSAGE = "그림이 QR 에 너무 많이 가려져요. 그림을 키우거나 옮겨 주세요.";

/** 잘라 낸 칸(mm)에서 구석 QR 자리를 뺀 넓이의 비율(0~1). */
export function markerVisibleRatio(crop: { x: number; y: number; w: number; h: number }, qr: { x: number; y: number; w: number; h: number } = AUTO_QR): number {
  const overlapW = Math.max(0, Math.min(crop.x + crop.w, qr.x + qr.w) - Math.max(crop.x, qr.x));
  const overlapH = Math.max(0, Math.min(crop.y + crop.h, qr.y + qr.h) - Math.max(crop.y, qr.y));
  const area = crop.w * crop.h;
  return area > 0 ? 1 - (overlapW * overlapH) / area : 0;
}

/** 가장 큰 그림 요소(인쇄 면적 기준). 그림이 하나도 없으면 null. */
export function largestImage(doc: PosterDoc): PosterImageElement | null {
  let best: PosterImageElement | null = null;
  for (const element of doc.els) {
    if (element.type !== "image") continue;
    if (!best || element.w * element.h > best.w * best.h) best = element;
  }
  return best;
}

/** 회전까지 감안한 바깥 상자(mm) — 포스터 밖으로 나간 부분은 잘라 낸다. */
export function markerCrop(element: PosterImageElement): { x: number; y: number; w: number; h: number } {
  const radians = element.rot * Math.PI / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  const w = element.w * cos + element.h * sin;
  const h = element.w * sin + element.h * cos;
  const cx = element.x + element.w / 2;
  const cy = element.y + element.h / 2;
  const left = Math.max(0, cx - w / 2);
  const top = Math.max(0, cy - h / 2);
  const right = Math.min(POSTER_PAGE.width, cx + w / 2);
  const bottom = Math.min(POSTER_PAGE.height, cy + h / 2);
  return { x: left, y: top, w: Math.max(1, right - left), h: Math.max(1, bottom - top) };
}

export function PosterConfirmPage({ workId }: { workId: string }) {
  const poster = usePoster(workId);
  if (poster.loading) return <LoadingPage label="인쇄 확정을 준비하는 중..." />;
  if (!poster.data) return <ErrorPage message={poster.error?.message ?? "포스터를 불러오지 못했어요."} />;
  return (
    <PosterResumeGate state={poster.data}>
      <Confirm workId={workId} state={poster.data} refresh={poster.refresh} />
    </PosterResumeGate>
  );
}

function Confirm({ workId, state, refresh }: { workId: string; state: PosterStateResponse; refresh: () => Promise<PosterStateResponse | null> }) {
  const [phase, setPhase] = useState<"idle" | "working">("idle");
  const [progress, setProgress] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const doc = withoutQr(state.doc ?? starterDoc(state));
  const target = largestImage(doc);
  const visible = target ? markerVisibleRatio(markerCrop(target)) : 0;
  const covered = Boolean(target) && visible < MARKER_MIN_VISIBLE;
  const ar = state.ar ?? { choice: "picture" as const, slot: 1 as const };
  const videoCut = ar.choice === "ai_video" ? state.cuts.find((cut) => cut.slot === (ar.slot ?? 1)) : undefined;
  const confirmed = state.confirmed_valid;
  const classQr = state.ar_scope === "class";

  const confirm = async () => {
    setPhase("working");
    setError(null);
    try {
      // 문서가 아직 한 번도 저장되지 않았으면(편집을 건너뛴 학생) 지금 모습을 먼저 저장한다.
      let revision = state.revision;
      if (!state.doc) revision = (await savePosterDoc(workId, doc, state.revision)).revision;
      setProgress("포스터를 그리는 중...");
      await loadPosterFonts();
      const images = await preloadImages(doc, posterImageLoader(workId, state));
      // 🔑 그림 하나라도 못 불러왔으면 확정하지 않는다 — 빈칸으로 구운 인식 파일에 그 그림 id 를
      //    붙여 보내면 인쇄물과 인식 파일이 또 어긋난다(QA01 · Codex 검수).
      const missing = doc.els.some((element) => element.type === "image" && !images.has(imageKey(element.src)));
      if (missing) throw new ApiError(0, "IMAGE_LOAD_FAILED", "그림을 불러오지 못했어요. 잠시 뒤 다시 눌러 주세요.");
      const pxPerMm = 1600 / POSTER_PAGE.width;
      const page = document.createElement("canvas");
      page.width = 1600;
      page.height = Math.round(POSTER_PAGE.height * pxPerMm);
      // 🔑 QR 없이 그린 뒤 QR 자리를 흰색으로 칠한다(D6) — 인쇄물의 QR 은 흰 바탕 위에 그려지므로 같은 자리다.
      const pageContext = page.getContext("2d")!;
      drawPosterToCanvas(pageContext, { doc, qrUrl: null, images }, pxPerMm);
      pageContext.fillStyle = "#ffffff";
      pageContext.fillRect(AUTO_QR.x * pxPerMm, AUTO_QR.y * pxPerMm, AUTO_QR.w * pxPerMm, AUTO_QR.h * pxPerMm);
      if (!target) throw new ApiError(422, "NO_IMAGE", "포스터에 그림이 있어야 AR 이 알아볼 수 있어요.");
      const crop = markerCrop(target);
      if (markerVisibleRatio(crop) < MARKER_MIN_VISIBLE) throw new ApiError(422, "QR_COVERS_PICTURE", QR_COVERS_PICTURE_MESSAGE);
      const cut = document.createElement("canvas");
      cut.width = Math.round(crop.w * pxPerMm);
      cut.height = Math.round(crop.h * pxPerMm);
      cut.getContext("2d")!.drawImage(page, crop.x * pxPerMm, crop.y * pxPerMm, cut.width, cut.height, 0, 0, cut.width, cut.height);
      const blob = await new Promise<Blob>((resolve, reject) => cut.toBlob((value) => value ? resolve(value) : reject(new Error("toBlob")), "image/png"));
      setProgress("AR 인식 파일을 만드는 중... 0%");
      const marker = await compileMarker(blob, { onProgress: (percent) => setProgress(`AR 인식 파일을 만드는 중... ${Math.round(percent)}%`) });
      setProgress("확정하는 중...");
      const form = new FormData();
      form.set("revision", String(revision));
      // 🔑 인식 파일을 구운 그림 = posterImageLoader 가 state.cuts 에서 고른 자산 — 그것을 그대로 보낸다.
      //    서버가 «지금 최신 그림» 으로 바꿔 적으면 인쇄물과 인식 파일이 어긋난다(QA01).
      form.set("assets", JSON.stringify(Object.fromEntries([1, 2, 3].map((slot) => [
        String(slot),
        state.cuts.find((cut) => cut.slot === slot)?.image_asset_id ?? null,
      ]))));
      form.set("marker", new Blob([marker as BlobPart], { type: "application/octet-stream" }), "poster.mind");
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/poster/confirm`, { method: "POST", body: form });
      await refresh();
      setProgress("");
    } catch (caught) {
      console.error("[poster-confirm]", caught);
      // 그림이 바뀌었으면 새 그림을 불러 둔다 — 학생은 «다시 확정» 만 누르면 된다.
      if (caught instanceof ApiError && caught.code === "STALE_ASSETS") await refresh().catch(() => null);
      setError(caught instanceof ApiError
        ? caught.code === "STALE_REVISION" ? "다른 곳에서 포스터를 고쳤어요. 새로고침한 뒤 다시 확정해 주세요."
          : caught.code === "STALE_ASSETS" ? "그림이 바뀌었어요. 새 그림으로 불러왔으니 다시 확정해 주세요." : caught.message
        : caught instanceof Error && caught.name === "MarkerCompileError" ? caught.message : "확정하지 못했어요. 다시 눌러 주세요.");
    } finally {
      setPhase("idle");
    }
  };

  const checks: Array<{ ok: boolean | "wait"; text: string }> = [
    { ok: true, text: `QR 코드는 오른쪽 아래 구석에 저절로 붙어요 (${(AUTO_QR.w / 10).toFixed(1)}cm)` },
    { ok: true, text: state.music ? `노래: ${state.music.title}` : "노래: 없이" },
    ...(ar.choice === "ai_video"
      ? [{ ok: videoCut?.clip_url ? true : videoCut?.video_job ? "wait" as const : false, text: videoCut?.clip_url ? "AI 영상: 완성" : videoCut?.video_job ? "AI 영상: 만드는 중 — 다 되면 저절로 붙어요" : "AI 영상: 아직 안 만들었어요 — 그림이 대신 떠요" }]
      : [{ ok: true, text: ar.choice === "my_mv" ? "AR: 내 뮤직비디오" : "AR: 그림 + 노래" }]),
    {
      ok: Boolean(target) && !covered,
      text: !target ? "AR 이 알아볼 그림이 없어요 — 편집에서 그림을 넣어 주세요"
        : covered ? `AR 이 알아볼 그림: ${QR_COVERS_PICTURE_MESSAGE}`
        : "AR 이 알아볼 그림: 포스터에서 가장 큰 그림",
    },
    { ok: true, text: classQr ? "QR 을 찍으면 우리 반 포스터 AR 이 열려요" : "QR 을 찍으면 내 포스터 AR 이 바로 열려요" },
  ];

  return (
    <StudioShell currentStep={4} wide imageRegenRemaining={state.image_regen_remaining} onStepClick={(step) => void goPosterStep(workId, step)}>
      <div className="poster-confirm">
        <div className="poster-confirm-poster"><PosterPreview state={state} doc={doc} /></div>
        <section className="poster-confirm-card">
          {confirmed ? (
            <>
              <h1 tabIndex={-1}>인쇄용으로 확정했어요</h1>
              <p>이제 포스터를 내려받아 인쇄해요. <b>확정한 뒤에 고치면 다시 확정하고 다시 인쇄</b>해야 AR 이 돼요.</p>
              <div className="poster-confirm-actions">
                <button className="button-primary full-width" type="button" onClick={() => setDownloadOpen(true)}>인쇄용 PDF 내려받기</button>
                <button className="button-secondary full-width" type="button" onClick={() => void goPosterStep(workId, 2)}>포스터 더 고치기</button>
              </div>
              <p className="poster-props-hint">{classQr ? "QR 을 찍으면 우리 반 포스터 AR 이 열려요. 카메라를 옆 포스터로 옮기면 그 친구 포스터가 나와요." : "QR 을 찍으면 바로 내 포스터 AR 이 열려요."} 다른 작품처럼 2월 말까지 볼 수 있어요(선생님이 1년 늘릴 수 있어요).</p>
            </>
          ) : (
            <>
              <h1 tabIndex={-1}>{state.confirmed_revision !== null ? "고친 포스터를 다시 확정할까요?" : "인쇄용으로 확정할까요?"}</h1>
              <p>확정하면 AR 이 <b>지금 이 포스터</b>를 알아보도록 준비해요. 확정한 뒤에 포스터를 고치면 <b>다시 확정하고 다시 인쇄</b>해야 AR 이 돼요.</p>
              <ul className="poster-checks">
                {checks.map((check) => (
                  <li key={check.text} className={check.ok === "wait" ? "is-wait" : check.ok ? "is-ok" : "is-no"}>{check.text}</li>
                ))}
              </ul>
              {error ? <InlineError>{error}</InlineError> : null}
              <button className="button-primary full-width" type="button" disabled={phase === "working" || !target || covered} onClick={() => void confirm()}>
                {phase === "working" ? <InlineSpinner label={progress || "준비하는 중..."} /> : "인쇄용으로 확정"}
              </button>
              <p className="poster-props-hint">인식 파일은 이 컴퓨터에서 만들어요. 30초~1분쯤 걸릴 수 있어요.</p>
            </>
          )}
        </section>
      </div>
      {downloadOpen ? <PosterDownloadDialog workId={workId} state={state} doc={doc} onClose={() => setDownloadOpen(false)} /> : null}
    </StudioShell>
  );
}
